/**
 * StripeFinanceActions.jsx – Phase 2A.1
 * 
 * Finance-Aktionen für einen Stripe-Kunden:
 * - Rückerstattung
 * - Rechnungskorrektur (Credit Note)
 * - Einmalige Rechnung
 * - Finance-Historie
 * 
 * Phase 2A.1: UI vollständig + Validierung + Bestätigungsdialog.
 * STRIPE_FINANCE_WRITES_ENABLED=false → zeigt "Derzeit deaktiviert"-Meldung.
 * Phase 2A.2: STRIPE_FINANCE_WRITES_ENABLED=true → echte Sandbox-Aktionen.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  RotateCcw, FileText, Receipt, History, ChevronDown, ChevronUp,
  AlertTriangle, Info, Loader2, X, CheckCircle2, Clock, Euro,
  Lock, Shield,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// ── Deaktiviert-Banner ────────────────────────────────────────────────────────
function WritesDisabledBanner({ show }) {
  if (!show) return null;
  return (
    <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700 rounded-xl">
      <Lock className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">Phase 2A.1 – Schreibaktionen deaktiviert</p>
        <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
          Alle Aktionen sind vorbereitet und validiert. Es wird jedoch kein Geld bewegt
          bis Phase 2A.2 freigegeben wird.
        </p>
      </div>
    </div>
  );
}

// ── Bestätigungsdialog ────────────────────────────────────────────────────────
function ConfirmDialog({ title, description, amount, customer, onConfirm, onCancel, loading }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="font-bold text-base">{title}</p>
              <p className="text-xs text-muted-foreground">{customer}</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">{description}</p>
          {amount && (
            <div className="p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-xl text-center">
              <p className="text-2xl font-bold text-red-700 dark:text-red-300">{amount}</p>
              <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">wird verarbeitet</p>
            </div>
          )}
        </div>
        <div className="flex gap-3 px-5 pb-5">
          <Button variant="outline" onClick={onCancel} disabled={loading} className="flex-1">Abbrechen</Button>
          <Button onClick={onConfirm} disabled={loading} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="finance-confirm-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Bestätigen
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Rückerstattungs-Modal ─────────────────────────────────────────────────────
function RefundModal({ customer, payments, onClose, onDone, writesEnabled = false }) {
  const [amount, setAmount] = useState('');
  const [reasonCode, setReasonCode] = useState('requested_by_customer'); // Fix 1: Enum
  const [internalComment, setInternalComment] = useState('');
  const [selectedPayment, setSelectedPayment] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);  // Fix 2: Max-Betrag
  const [opId] = useState(() => `finop_${Math.random().toString(36).substr(2,16)}`); // D: Op-ID

  // Fix 2: PaymentIntent-Info laden wenn Zahlung ausgewählt
  useEffect(() => {
    if (!selectedPayment) { setPaymentInfo(null); return; }
    // In Phase 2A.1 deaktiviert – zeigt Zahlungsdaten aus lokaler Liste
    const payment = (payments || []).find(p => p.id === selectedPayment);
    if (payment) {
      setPaymentInfo({
        amount_paid: payment.amount_cents || 0,
        amount_refunded: 0,
        remaining: payment.amount_cents || 0,
      });
    }
  }, [selectedPayment, payments]);

  const reasonOptions = {
    'requested_by_customer': 'Auf Kundenwunsch',
    'duplicate':             'Doppelzahlung',
    'fraudulent':            'Verdacht auf Betrug',
  };

  const maxCents = paymentInfo?.remaining || 0;
  const enteredCents = parseFloat((amount||'0').replace(',','.')) * 100;

  const handleSubmit = async () => {
    const euros = parseFloat(amount.replace(',', '.'));
    if (isNaN(euros) || euros <= 0) { toast.error('Gültigen Betrag eingeben'); return; }
    if (!selectedPayment) { toast.error('Bitte Zahlung auswählen'); return; }
    // Fix 2: Client-seitige Vorab-Prüfung
    if (maxCents > 0 && Math.round(euros * 100) > maxCents) {
      toast.error(`Maximaler Erstattungsbetrag: ${(maxCents/100).toFixed(2)} €`);
      return;
    }
    setConfirm(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setConfirm(false);
    try {
      const euros = parseFloat(amount.replace(',', '.'));
      const res = await axios.post(`${API}/api/superadmin/finance/refund`, {
        customer_id: customer.id,
        stripe_customer_id: customer.stripe_customer_id,
        payment_intent_id: selectedPayment,
        amount_cents: Math.round(euros * 100),
        currency: 'eur',
        reason_code: reasonCode,             // Fix 1: Enum, nicht Freitext
        internal_comment: internalComment.trim() || undefined,
        confirmed: true,
        operation_id: opId,                  // D: Operation-ID
      }, { headers: authHeader() });
      setResult(res.data);
      if (res.data.status === 'disabled') {
        toast.info('Validiert – Schreibaktionen derzeit deaktiviert');
      } else {
        toast.success('Rückerstattung erfolgreich');
        onDone();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler');
    } finally { setLoading(false); }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
        <div className="bg-white dark:bg-gray-900 w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <RotateCcw className="h-4 w-4 text-red-500" />
              <h2 className="text-base font-bold">Geld zurückerstatten</h2>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="h-5 w-5" /></button>
          </div>
          <div className="px-5 py-4 space-y-4">
            <WritesDisabledBanner show={!writesEnabled} />
            <div className="p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-xl">
              <p className="text-xs text-red-700 dark:text-red-400 font-medium">
                Echtes Geld geht zurück auf Karte/SEPA. Nicht rückgängig machbar.
              </p>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Zahlung auswählen *</Label>
              <select value={selectedPayment} onChange={e => setSelectedPayment(e.target.value)}
                className="h-9 w-full rounded-lg border bg-background px-3 text-sm" data-testid="refund-payment-select">
                <option value="">Zahlung auswählen…</option>
                {(payments || []).filter(p => p.status === 'succeeded').map(p => (
                  <option key={p.id} value={p.id}>
                    {p.amount_display} – {p.date} – {p.payment_method}
                  </option>
                ))}
              </select>
            </div>

            {/* Fix 2: Max-Betrag Anzeige */}
            {paymentInfo && (
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="p-2 bg-gray-50 dark:bg-gray-800/40 rounded-lg text-center">
                  <p className="text-muted-foreground">Bezahlt</p>
                  <p className="font-semibold">{(paymentInfo.amount_paid/100).toFixed(2)} €</p>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-800/40 rounded-lg text-center">
                  <p className="text-muted-foreground">Erstattet</p>
                  <p className="font-semibold">{(paymentInfo.amount_refunded/100).toFixed(2)} €</p>
                </div>
                <div className="p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-lg text-center">
                  <p className="text-muted-foreground">Noch möglich</p>
                  <p className="font-bold text-green-700 dark:text-green-300">{(paymentInfo.remaining/100).toFixed(2)} €</p>
                </div>
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs">Erstattungsbetrag (€) *</Label>
              <Input value={amount} onChange={e => setAmount(e.target.value)}
                placeholder={maxCents > 0 ? `Max. ${(maxCents/100).toFixed(2)} €` : '0,00'}
                data-testid="refund-amount-input"
                className={maxCents > 0 && enteredCents > maxCents ? 'border-red-400' : ''} />
              {maxCents > 0 && enteredCents > maxCents && (
                <p className="text-xs text-red-600">Betrag überschreitet das Maximum</p>
              )}
            </div>

            {/* Fix 1: Reason als Dropdown */}
            <div className="space-y-1">
              <Label className="text-xs">Erstattungsgrund *</Label>
              <select value={reasonCode} onChange={e => setReasonCode(e.target.value)}
                className="h-9 w-full rounded-lg border bg-background px-3 text-sm" data-testid="refund-reason-select">
                {Object.entries(reasonOptions).map(([code, label]) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Interner Kommentar (nur für Audit-Log, nicht an Stripe)</Label>
              <Input value={internalComment} onChange={e => setInternalComment(e.target.value)}
                placeholder="z.B. Kulanz wegen Störung am 12.07." />
            </div>

            <p className="text-xs text-muted-foreground">Op-ID: {opId}</p>

            {result?.status === 'disabled' && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 rounded-xl">
                <p className="text-xs font-semibold text-amber-800">Validiert ✓</p>
                <p className="text-xs text-amber-700 mt-0.5">{result.message}</p>
              </div>
            )}
          </div>
          <div className="flex gap-3 px-5 pb-4">
            <Button variant="outline" onClick={onClose} className="flex-1">Abbrechen</Button>
            <Button onClick={handleSubmit} disabled={loading || (maxCents > 0 && enteredCents > maxCents)}
              className="flex-1 bg-red-600 hover:bg-red-700" data-testid="refund-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Rückerstattung prüfen
            </Button>
          </div>
        </div>
      </div>
      {confirm && (
        <ConfirmDialog title="Rückerstattung ausführen"
          description={`${reasonOptions[reasonCode]} – Echtes Geld geht zurück. Nicht rückgängig.`}
          amount={amount ? `${parseFloat(amount.replace(',','.')).toFixed(2)} €` : ''}
          customer={customer.company_name} onConfirm={handleConfirm}
          onCancel={() => setConfirm(false)} loading={loading} />
      )}
    </>
  );
}

// ── Credit-Note-Modal ─────────────────────────────────────────────────────────
function CreditNoteModal({ customer, invoices, onClose, onDone, writesEnabled = false }) {
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const [internalComment, setInternalComment] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [opId] = useState(() => `finop_${Math.random().toString(36).substr(2,16)}`);

  // Fix 3: Nur paid/open Rechnungen anzeigen
  const validInvoices = (invoices || []).filter(inv => ['paid', 'open'].includes(inv.status));

  const handleSubmit = async () => {
    const euros = parseFloat(amount.replace(',', '.'));
    if (isNaN(euros) || euros <= 0) { toast.error('Gültigen Betrag eingeben'); return; }
    if (!selectedInvoice) { toast.error('Bitte Rechnung auswählen'); return; }
    setConfirm(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setConfirm(false);
    try {
      const euros = parseFloat(amount.replace(',', '.'));
      const res = await axios.post(`${API}/api/superadmin/finance/credit-note`, {
        customer_id: customer.id,
        stripe_customer_id: customer.stripe_customer_id,
        invoice_id: selectedInvoice,
        amount_cents: Math.round(euros * 100),
        currency: 'eur',
        memo: memo.trim() || undefined,
        internal_comment: internalComment.trim() || undefined,
        confirmed: true,
        operation_id: opId,
      }, { headers: authHeader() });
      setResult(res.data);
      if (res.data.status === 'disabled') {
        toast.info('Validiert – Schreibaktionen derzeit deaktiviert');
      } else {
        toast.success('Rechnungskorrektur erstellt');
        onDone();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler');
    } finally { setLoading(false); }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
        <div className="bg-white dark:bg-gray-900 w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-500" />
              <h2 className="text-base font-bold">Rechnung korrigieren</h2>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="h-5 w-5" /></button>
          </div>
          <div className="px-5 py-4 space-y-4">
            <WritesDisabledBanner show={!writesEnabled} />
            <div className="p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-700 rounded-xl">
              <p className="text-xs text-blue-700 dark:text-blue-400">
                <strong>Rechnungskorrektur:</strong> Eine bereits gestellte Rechnung wird korrigiert.
                Das Guthaben wird mit der nächsten Rechnung verrechnet oder erstattet.
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Rechnung auswählen * (nur bezahlte / offene)</Label>
              <select value={selectedInvoice} onChange={e => setSelectedInvoice(e.target.value)}
                className="h-9 w-full rounded-lg border bg-background px-3 text-sm" data-testid="creditnote-invoice-select">
                <option value="">Rechnung auswählen…</option>
                {/* Fix 3: Nur paid/open */}
                {validInvoices.map(inv => (
                  <option key={inv.id} value={inv.id}>
                    {inv.number || inv.id?.slice(-8)} – {inv.amount_due_display} – {inv.status_de}
                  </option>
                ))}
              </select>
              {validInvoices.length === 0 && (
                <p className="text-xs text-amber-600">Keine erstattungsfähigen Rechnungen (nur bezahlt/offen möglich)</p>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Korrekturbetrag (€) *</Label>
              <Input value={amount} onChange={e => setAmount(e.target.value)} placeholder="z.B. 10,00" data-testid="creditnote-amount-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Hinweis auf Credit Note (max. 64 Zeichen, erscheint auf Dokument)</Label>
              <Input value={memo} onChange={e => setMemo(e.target.value.slice(0,64))} placeholder="z.B. Korrektur Rechnungsbetrag" data-testid="creditnote-memo-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Interner Kommentar (nur Audit-Log)</Label>
              <Input value={internalComment} onChange={e => setInternalComment(e.target.value)} placeholder="Für interne Notizen" />
            </div>
            <p className="text-xs text-muted-foreground">Op-ID: {opId}</p>
            {result?.status === 'disabled' && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-xs font-semibold text-amber-800">Validiert ✓</p>
                <p className="text-xs text-amber-700 mt-0.5">{result.message}</p>
              </div>
            )}
          </div>
          <div className="flex gap-3 px-5 pb-4">
            <Button variant="outline" onClick={onClose} className="flex-1">Abbrechen</Button>
            <Button onClick={handleSubmit} disabled={loading || validInvoices.length === 0}
              className="flex-1" data-testid="creditnote-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Korrektur prüfen
            </Button>
          </div>
        </div>
      </div>
      {confirm && (
        <ConfirmDialog title="Rechnungskorrektur erstellen"
          description="Eine Credit Note wird erstellt und mit zukünftigen Rechnungen verrechnet."
          amount={amount ? `${parseFloat(amount.replace(',','.')).toFixed(2)} €` : ''}
          customer={customer.company_name} onConfirm={handleConfirm}
          onCancel={() => setConfirm(false)} loading={loading} />
      )}
    </>
  );
}

// ── Einmalige Rechnung ────────────────────────────────────────────────────────
function InvoiceCreateModal({ customer, onClose, onDone, writesEnabled = false }) {
  const [label, setLabel] = useState('');
  const [items, setItems] = useState([{ description: '', quantity: 1, unit_amount: '' }]);
  const [comment, setComment] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const totalCents = items.reduce((sum, item) => {
    const a = parseFloat((item.unit_amount || '0').replace(',', '.'));
    return sum + (isNaN(a) ? 0 : Math.round(a * 100) * (parseInt(item.quantity) || 1));
  }, 0);

  const handleSubmit = async () => {
    if (!label.trim()) { toast.error('Bezeichnung fehlt'); return; }
    if (!customer.stripe_customer_id) { toast.error('Kunde hat keine Stripe-ID'); return; }
    const validItems = items.filter(i => i.description.trim() && parseFloat((i.unit_amount || '0').replace(',','.')) > 0);
    if (validItems.length === 0) { toast.error('Mind. eine gültige Position erforderlich'); return; }
    setConfirm(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setConfirm(false);
    try {
      const validItems = items.filter(i => i.description.trim() && parseFloat((i.unit_amount || '0').replace(',','.')) > 0);
      const res = await axios.post(`${API}/api/superadmin/finance/invoice/create`, {
        customer_id: customer.id,
        stripe_customer_id: customer.stripe_customer_id,
        invoice_label: label.trim(),
        items: validItems.map(i => ({
          description: i.description,
          quantity: parseInt(i.quantity) || 1,
          unit_amount_cents: Math.round(parseFloat(i.unit_amount.replace(',','.')) * 100),
        })),
        internal_comment: comment.trim() || undefined,
        confirmed: true,
      }, { headers: authHeader() });
      setResult(res.data);
      if (res.data.status === 'disabled') {
        toast.info('Validiert – Schreibaktionen derzeit deaktiviert');
      } else {
        toast.success('Rechnung erstellt');
        onDone();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler');
    } finally { setLoading(false); }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
        <div className="bg-white dark:bg-gray-900 w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800 sticky top-0 bg-white dark:bg-gray-900">
            <div className="flex items-center gap-2">
              <Receipt className="h-4 w-4 text-indigo-500" />
              <h2 className="text-base font-bold">Einmalige Rechnung</h2>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="h-5 w-5" /></button>
          </div>
          <div className="px-5 py-4 space-y-4">
            <WritesDisabledBanner show={!writesEnabled} />
            <div className="space-y-1">
              <Label className="text-xs">Interne Bezeichnung *</Label>
              <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="z.B. Einrichtungsgebühr, Schulung" data-testid="invoice-label-input" />
            </div>
            {/* Positionen */}
            <div className="space-y-2">
              <Label className="text-xs">Rechnungspositionen *</Label>
              {items.map((item, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <Input value={item.description} onChange={e => {
                    const n=[...items]; n[i]={...n[i],description:e.target.value}; setItems(n);
                  }} placeholder="Beschreibung" className="flex-1 text-sm" data-testid={`invoice-item-desc-${i}`} />
                  <Input type="number" min="1" value={item.quantity} onChange={e => {
                    const n=[...items]; n[i]={...n[i],quantity:e.target.value}; setItems(n);
                  }} className="w-16 text-sm" placeholder="Qty" />
                  <Input value={item.unit_amount} onChange={e => {
                    const n=[...items]; n[i]={...n[i],unit_amount:e.target.value}; setItems(n);
                  }} placeholder="€" className="w-24 text-sm" data-testid={`invoice-item-amount-${i}`} />
                  {items.length > 1 && (
                    <button onClick={() => setItems(items.filter((_,j)=>j!==i))} className="p-1.5 hover:bg-red-50 rounded text-red-400">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              <button onClick={() => setItems([...items,{description:'',quantity:1,unit_amount:''}])}
                className="text-xs text-indigo-600 hover:underline">+ Position hinzufügen</button>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/40 rounded-xl">
              <span className="text-sm text-muted-foreground">Netto-Gesamtbetrag</span>
              <span className="text-base font-bold">{(totalCents/100).toFixed(2)} € zzgl. MwSt.</span>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Interner Kommentar (optional)</Label>
              <Input value={comment} onChange={e => setComment(e.target.value)} placeholder="Für interne Notizen" />
            </div>
            {result?.status === 'disabled' && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-xs font-semibold text-amber-800">Validiert ✓ | Gesamtbetrag: {(result.details?.would_total_cents||0)/100} € | Positionen: {result.details?.would_create_items}</p>
                <p className="text-xs text-amber-700 mt-0.5">{result.message}</p>
              </div>
            )}
          </div>
          <div className="flex gap-3 px-5 pb-4">
            <Button variant="outline" onClick={onClose} className="flex-1">Abbrechen</Button>
            <Button onClick={handleSubmit} disabled={loading} className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="invoice-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Rechnung prüfen
            </Button>
          </div>
        </div>
      </div>
      {confirm && (
        <ConfirmDialog
          title="Einmalige Rechnung erstellen"
          description="Eine neue Rechnung wird für diesen Kunden erstellt. Sie wird zunächst als Entwurf gespeichert."
          amount={totalCents > 0 ? `${(totalCents/100).toFixed(2)} € netto` : ''}
          customer={customer.company_name}
          onConfirm={handleConfirm}
          onCancel={() => setConfirm(false)}
          loading={loading}
        />
      )}
    </>
  );
}

// ── Finance-Historie ──────────────────────────────────────────────────────────
function FinanceHistory({ customerId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customerId) return;
    setLoading(true);
    axios.get(`${API}/api/superadmin/finance/history/${customerId}`, { headers: authHeader() })
      .then(r => setEntries(r.data.entries || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [customerId]);

  const ACTION_LABELS = {
    refund_requested: 'Rückerstattung',
    credit_note_requested: 'Rechnungskorrektur',
    invoice_create_requested: 'Einmalige Rechnung',
  };

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>;
  if (entries.length === 0) return <p className="text-xs text-muted-foreground text-center py-4">Noch keine Finance-Aktionen</p>;

  return (
    <div className="space-y-2">
      {entries.slice(0, 10).map((e, i) => (
        <div key={e.id || i} className={`flex items-start justify-between gap-2 p-2.5 rounded-lg text-xs ${
          e.dry_run ? 'bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700' : 'bg-green-50 dark:bg-green-900/10 border border-green-200'
        }`}>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              {e.dry_run ? <Clock className="h-3 w-3 text-amber-600" /> : <CheckCircle2 className="h-3 w-3 text-green-600" />}
              <span className="font-medium">{ACTION_LABELS[e.action] || e.action}</span>
              {e.dry_run && <span className="px-1 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px]">Testlauf</span>}
            </div>
            <p className="text-muted-foreground mt-0.5 truncate">
              {e.reason} · {e.amount_cents ? `${(e.amount_cents/100).toFixed(2)} €` : ''} · {e.created_at ? new Date(e.created_at).toLocaleDateString('de-DE') : ''}
            </p>
          </div>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
            e.dry_run ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
          }`}>{e.stripe_result_status}</span>
        </div>
      ))}
    </div>
  );
}

// ── Haupt-Aktionsbereich ──────────────────────────────────────────────────────
export default function StripeFinanceActions({ customer, invoices, payments, writesEnabled }) {
  const [activeModal, setActiveModal] = useState(null); // 'refund' | 'creditnote' | 'invoice' | 'history'
  const [showHistory, setShowHistory] = useState(false);

  if (!customer?.stripe_customer_id) {
    return (
      <div className="p-3 bg-gray-50 dark:bg-gray-800/40 rounded-xl">
        <p className="text-xs text-muted-foreground text-center">
          Kein Stripe-Account – Finance-Aktionen nicht verfügbar
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Aktions-Buttons */}
      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Finance-Aktionen</p>
      <div className="grid grid-cols-1 gap-2">
        <button onClick={() => setActiveModal('refund')}
          className="flex items-center gap-2.5 p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-xl hover:bg-red-100 transition-colors text-left"
          data-testid="open-refund-btn">
          <RotateCcw className="h-4 w-4 text-red-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-700 dark:text-red-300">Geld zurückerstatten</p>
            <p className="text-xs text-red-600 dark:text-red-400">Echte Zahlung zurück auf Karte/SEPA</p>
          </div>
        </button>

        <button onClick={() => setActiveModal('creditnote')}
          className="flex items-center gap-2.5 p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-xl hover:bg-blue-100 transition-colors text-left"
          data-testid="open-creditnote-btn">
          <FileText className="h-4 w-4 text-blue-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">Rechnung korrigieren</p>
            <p className="text-xs text-blue-600 dark:text-blue-400">Credit Note für bestehende Rechnung</p>
          </div>
        </button>

        <button onClick={() => setActiveModal('invoice')}
          className="flex items-center gap-2.5 p-3 bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-200 dark:border-indigo-800 rounded-xl hover:bg-indigo-100 transition-colors text-left"
          data-testid="open-invoice-btn">
          <Receipt className="h-4 w-4 text-indigo-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">Einmalige Rechnung</p>
            <p className="text-xs text-indigo-600 dark:text-indigo-400">Einrichtung, Schulung, Sonderleistung</p>
          </div>
        </button>
      </div>

      {/* Finance-Historie */}
      <button onClick={() => setShowHistory(v => !v)}
        className="flex items-center justify-between w-full p-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/40 rounded-xl transition-colors">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs font-medium">Finance-Historie</span>
        </div>
        {showHistory ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
      </button>
      {showHistory && <FinanceHistory customerId={customer.id} />}

      {/* Modals */}
      {activeModal === 'refund' && (
        <RefundModal customer={customer} payments={payments} onClose={() => setActiveModal(null)} onDone={() => setActiveModal(null)} writesEnabled={writesEnabled} />
      )}
      {activeModal === 'creditnote' && (
        <CreditNoteModal customer={customer} invoices={invoices} onClose={() => setActiveModal(null)} onDone={() => setActiveModal(null)} writesEnabled={writesEnabled} />
      )}
      {activeModal === 'invoice' && (
        <InvoiceCreateModal customer={customer} onClose={() => setActiveModal(null)} onDone={() => setActiveModal(null)} writesEnabled={writesEnabled} />
      )}
    </div>
  );
}
